import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import deAccessibility from "@/locales/de/accessibility.json";
import deActivityLog from "@/locales/de/activity-log.json";
import deAuth from "@/locales/de/auth.json";
import deClimateAction from "@/locales/de/climate-action.json";
import deCommon from "@/locales/de/common.json";
import deDataRequest from "@/locales/de/data-request.json";
import deEntitlement from "@/locales/de/entitlement.json";
import deFocusArea from "@/locales/de/focus-area.json";
import deIndicator from "@/locales/de/indicator.json";
import deInventory from "@/locales/de/inventory.json";
import deMilestone from "@/locales/de/milestone.json";
import deMunicipalProperty from "@/locales/de/municipal-property.json";
import deOffering from "@/locales/de/offering.json";
import deProject from "@/locales/de/project.json";
import dePublicDashboard from "@/locales/de/public-dashboard.json";
import deScenario from "@/locales/de/scenario.json";
import deTask from "@/locales/de/task.json";
import deTeam from "@/locales/de/team.json";
import deTrash from "@/locales/de/trash.json";
import deTwoFactorAuth from "@/locales/de/two-factor-auth.json";
import deUser from "@/locales/de/user.json";
import deWorkspace from "@/locales/de/workspace.json";

export const deResourceBundle = {
  common: deCommon,
  accessibility: deAccessibility,
  auth: deAuth,
  workspace: deWorkspace,
  user: deUser,
  "climate-action": deClimateAction,
  project: deProject,
  task: deTask,
  team: deTeam,
  "focus-area": deFocusArea,
  milestone: deMilestone,
  indicator: deIndicator,
  "activity-log": deActivityLog,
  inventory: deInventory,
  offering: deOffering,
  scenario: deScenario,
  "public-dashboard": dePublicDashboard,
  "two-factor-auth": deTwoFactorAuth,
  "data-request": deDataRequest,
  "municipal-property": deMunicipalProperty,
  trash: deTrash,
  entitlement: deEntitlement,
} as const;

export type DeResourceBundle = typeof deResourceBundle;

export type I18nNamespace = keyof DeResourceBundle;

export const I18N_NAMESPACE_LIST = Object.keys(deResourceBundle) as ReadonlyArray<I18nNamespace>;

i18n.use(initReactI18next).init({
  lng: "de",
  fallbackLng: "de",
  defaultNS: "common",
  ns: [...I18N_NAMESPACE_LIST],
  resources: {
    de: deResourceBundle,
  },
  interpolation: {
    escapeValue: false, // React already escapes rendered output
  },
});

/**
 * The Vite-specific import.meta.glob call lives in a separate module
 * (`locale-modules.ts`) so Jest can mock it without hitting a syntax error.
 */
export async function loadLocaleBundle(locale: "en" | "fr"): Promise<void> {
  const allLoaded = I18N_NAMESPACE_LIST.every((ns) => i18n.hasResourceBundle(locale, ns));
  if (allLoaded) {
    return;
  }

  const { localeModules } = await import("@/lib/locale-modules");
  const modules = localeModules[locale] ?? {};

  const resultList = await Promise.allSettled(
    I18N_NAMESPACE_LIST.map(async (ns) => {
      const key = `../locales/${locale}/${ns}.json`;
      const loader = modules[key];
      if (!loader) {
        throw new Error(`No locale module found for "${key}"`);
      }
      const data = await loader();
      return { ns, data: data as Record<string, unknown> };
    }),
  );

  for (const result of resultList) {
    if (result.status === "fulfilled") {
      i18n.addResourceBundle(locale, result.value.ns, result.value.data, true, true);
    }
  }

  const failures = resultList
    .map((r, i) => (r.status === "rejected" ? `${I18N_NAMESPACE_LIST[i]}: ${r.reason}` : null))
    .filter(Boolean);
  if (failures.length > 0) {
    console.warn(`Failed to load ${failures.length} namespace(s) for locale "${locale}": ${failures.join(", ")}`);
  }

  // A partial load still leaves most of the UI translated, so it stays a warning. A total
  // failure does not: every string falls back to the previous language, and callers that
  // assume success would switch i18n and the document lang to a locale nothing renders in.
  if (resultList.length > 0 && failures.length === resultList.length) {
    throw new Error(`No namespace could be loaded for locale "${locale}"`);
  }
}

export default i18n;
